import FullScreenLoader from "../components/utils/FullScreenLoader"
import { MyDispatch, MySelector } from "../redux/store"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useState, useMemo } from "react"
import { useDispatch } from "react-redux"
import LayoutWrapper from "../components/Photos/LayoutWrapper"
import NoFiles from "../components/gallery/NoFiles"
import {
  resetSelectedPhotos,
  setActiveAlbumId,
  setFolderType,
  setSelectionMode,
} from "../redux/utilSlice"
import {
  getActiveAlbumFiles,
  getPhysicalAlbums,
  setActiveAlbumFiles,
} from "../redux/gallerySlice"
import Dropdown from "../components/gallery/Dropdown"
import { PhysicalAlbum as PhysicalAlbumType } from "../types/physicalAlbum"
import backIcon from "../assets/photo/back.svg"
import backLight from "../assets/photo/back_dark.svg"

type SortOption = "name-asc" | "name-desc" | "time-asc" | "time-desc"

const PhysicalAlbumDetail = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<MyDispatch>()
  const { albumId } = useParams()
  const [sortBy, setSortBy] = useState<SortOption>("time-desc")

  const { activeAlbumFiles, physicalAlbums } = MySelector((state) => state.gallery)
  const { selectionMode } = MySelector((state) => state.util)
  const { category, eventId, userId } = MySelector((state) => state.event)
  const { theme } = MySelector((state) => state.theme)

  const album: PhysicalAlbumType | undefined = useMemo(
    () => physicalAlbums?.find((a: PhysicalAlbumType) => a.id === albumId),
    [physicalAlbums, albumId]
  )

  // guests can't access
  useEffect(() => {
    if (!category || !eventId) return
    if (category === "guest") navigate(`/${eventId}/gallery`)
  }, [category, eventId, navigate])

  // set context + load this album's files
  useEffect(() => {
    if (!albumId) return
    dispatch(setFolderType("physical"))
    dispatch(setActiveAlbumId(albumId))
    dispatch(setSelectionMode(false))
    dispatch(setActiveAlbumFiles(null)) // show loader instead of previous album's photos
    dispatch(
      getActiveAlbumFiles({ physicalAlbumId: albumId, userId: userId || undefined })
    )
  }, [albumId, userId, dispatch])

  // ensure album metadata (name/count) is available on direct navigation
  useEffect(() => {
    if (category !== "host") return
    if (!eventId) return
    if (!physicalAlbums)
      dispatch(
        getPhysicalAlbums({ eventId: eventId || "", userId: userId || undefined })
      )
  }, [category, eventId, userId, physicalAlbums, dispatch])

  // leave selection mode when navigating away from the album
  useEffect(() => {
    return () => {
      dispatch(setSelectionMode(false))
      dispatch(resetSelectedPhotos())
    }
  }, [dispatch])

  const sortedFiles = useMemo(() => {
    if (!activeAlbumFiles || activeAlbumFiles.length === 0) return activeAlbumFiles

    return [...activeAlbumFiles].sort((a: any, b: any) => {
      switch (sortBy) {
        case "name-asc":
          return (a.name || "").localeCompare(b.name || "")
        case "name-desc":
          return (b.name || "").localeCompare(a.name || "")
        case "time-asc":
          return new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
        case "time-desc":
          return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
        default:
          return 0
      }
    })
  }, [activeAlbumFiles, sortBy])

  if (!activeAlbumFiles) return <FullScreenLoader />

  return (
    <div
      className={`flex flex-col flex-1 items-center ${
        selectionMode ? "" : "mt-[50px]"
      } lg:mt-0`}
    >
      {/* Header: back + name + actions */}
      <div className="flex items-center justify-between w-full px-4 m-4 lg:mt-6 lg:mb-6 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={theme === "dark" ? backIcon : backLight}
            alt="back"
            className="h-5 w-5 cursor-pointer shrink-0"
            onClick={() => navigate(`/${eventId}/physical-album`)}
          />
          <p className="font-[500] text-newOffBlack dark:text-offWhite text-sm md:text-base xl:text-lg truncate">
            {album?.name || "Album"}
          </p>
          <span className="text-newDarkBlack dark:text-golden text-sm shrink-0">
            ({activeAlbumFiles?.length || 0})
          </span>
        </div>

        {activeAlbumFiles && activeAlbumFiles.length > 0 && (
          <button
            onClick={() => {
              if (selectionMode) dispatch(resetSelectedPhotos())
              dispatch(setSelectionMode(!selectionMode))
            }}
            className={`shrink-0 flex items-center justify-center rounded-xl py-1.5 px-3 text-sm ${
              selectionMode
                ? "bg-golden text-white"
                : "border border-newOffBlack dark:border-white text-newOffBlack dark:text-white"
            }`}
            title="Select photos for bulk actions"
          >
            Select
          </button>
        )}
      </div>

      {activeAlbumFiles && activeAlbumFiles?.length > 0 && (
        <div className="flex justify-end px-4 mb-4 w-full">
          <Dropdown
            width="180px"
            zIndex={1000}
            trigger={
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-newOffBlack dark:text-offWhite hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                <span>Sort by</span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            }
          >
            <li
              onClick={(e) => {
                e.stopPropagation()
                setSortBy("time-desc")
              }}
              className={`px-4 py-2 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${sortBy === "time-desc" ? "bg-gray-100 dark:bg-gray-700" : ""} text-newOffBlack dark:text-offWhite`}
            >
              Newest First
            </li>
            <li
              onClick={(e) => {
                e.stopPropagation()
                setSortBy("time-asc")
              }}
              className={`px-4 py-2 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${sortBy === "time-asc" ? "bg-gray-100 dark:bg-gray-700" : ""} text-newOffBlack dark:text-offWhite`}
            >
              Oldest First
            </li>
            <li
              onClick={(e) => {
                e.stopPropagation()
                setSortBy("name-asc")
              }}
              className={`px-4 py-2 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${sortBy === "name-asc" ? "bg-gray-100 dark:bg-gray-700" : ""} text-newOffBlack dark:text-offWhite`}
            >
              Name A-Z
            </li>
            <li
              onClick={(e) => {
                e.stopPropagation()
                setSortBy("name-desc")
              }}
              className={`px-4 py-2 cursor-pointer text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${sortBy === "name-desc" ? "bg-gray-100 dark:bg-gray-700" : ""} text-newOffBlack dark:text-offWhite`}
            >
              Name Z-A
            </li>
          </Dropdown>
        </div>
      )}

      <div className="h-full w-full">
        {activeAlbumFiles && activeAlbumFiles?.length > 0 ? (
          <LayoutWrapper overridePhotos={sortedFiles || []} />
        ) : (
          <NoFiles />
        )}
      </div>
    </div>
  )
}

export default PhysicalAlbumDetail
